<template>
  <v-dialog
    v-model="dialog"
    persistent
    scrollable
    :fullscreen="$vuetify.display.xs"
    :width="$vuetify.display.smAndUp ? 600 : 'auto'"
    style="z-index: 1100 !important;"
  >
    <template v-slot:activator="{ props }">
      <slot name="activator" v-bind="{ props }" />
    </template>

    <v-form ref="form" data-cy="dialog-convertImage-modal">
      <v-card>
        <v-card-title class="d-flex justify-space-between"
                      data-cy="dialog-convertImage-title">
          {{ $t('routes.pod.menuMediaConverter.convertImageFile') }}

          <v-btn icon @click="dialog = false" :aria-label="$t('common.close')">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text scrollable class="pt-4 scrollable">
          <div data-cy="podMenu-tools-convertImage-selectFormat">
            <v-select
              v-model="selectedFormat"
              v-bind:items="imageFormats"
              v-bind:label="$t('routes.pod.menuMediaConverter.selectFormat')"
              v-bind:rules="selectedFormatRules"
              :menu-props="{ maxHeight: 320 }"
              :item-props="() => ({ tabindex: 0 })"
              variant="solo-filled" flat
              required
              single-line
            ></v-select>
          </div>

          <div data-cy="podMenu-tools-convertImage-selectQuality">
            <v-select
              v-model="selectedQuality"
              v-bind:items="imageQuality"
              v-bind:label="$t('routes.pod.menuMediaConverter.selectQuality')"
              v-bind:rules="selectedQualityRules"
              item-title="name"
              item-value="value"
              :menu-props="{ maxHeight: 320 }"
              :item-props="() => ({ tabindex: 0 })"
              variant="solo-filled" flat
              required
              single-line
            ></v-select>
          </div>

          <UtilityPodsErrors class="mt-2"></UtilityPodsErrors>

          <div v-show="successMessage" class="bg-success" data-cy="convertFile-successMessage">
            {{ successMessage }}
          </div>
          <div v-show="errorMessage" class="bg-error" data-cy="convertFile-errorMessage">
            {{ errorMessage }}
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            class="bg-primary"
            @click="runJobs"
            elevation=0
            v-bind:disabled="isRunJobsDisabled"
            data-cy="dialog-convertFile-ok">{{ $t('routes.pod.menuMediaConverter.convert') }}
          </v-btn>
          <v-btn variant="text" @click="dialog = false" data-cy="dialog-convertFile-false">
            {{ closeButtonLabel }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>

  </v-dialog>
</template>

<script>
import { usePodStore } from '@/stores/PodStore.js';
import { useServiceStore } from '@/stores/ServiceStore.js'
import DialogActionKeys from '@/components/mixin/DialogActionKeys.js';
import DialogModel from '@/components/mixin/DialogModel.js';

export default {
  mixins: [DialogActionKeys, DialogModel],
  name: "ConvertImageFileDialog",
  props: {
    pods: {
      type: Array,
      required: true
    },
  },
  data: function() {
    return {
      selectedFormat: "png",
      selectedQuality: "best",

      closeButtonLabel: this.$t('common.cancel'),

      errorMessage: "",
      successMessage: "",
      preventDoubleclick: false,

      selectedFormatRules: [
        v => !!v || this.$t('routes.pod.menuMediaConverter.errorSelectedFileFormat')
      ],
      selectedQualityRules: [
        v => !!v || this.$t('routes.pod.menuMediaConverter.errorSelectedQuality')
      ],
    };
  },
  mounted: function() {
    const serviceStore = useServiceStore();
    serviceStore.resetUtilityPods();
  },
  watch: {
    dialog(newValue) {
      if (newValue) {
        this.resetForm();
        this.updatePodsUtility();
      }
    },
  },
  methods: {
    resetForm() {
      if (this.$refs.form) {
        this.$refs.form.reset();
      }

      this.$nextTick(() => {
        this.errorMessage = '';
        this.successMessage = '';
        this.preventDoubleclick = false;

        this.selectedFormat = "png";
        this.selectedQuality = "best";

        this.closeButtonLabel = this.$t('common.cancel');

        const serviceStore = useServiceStore();
        serviceStore.resetUtilityPods();
      });
    },
    updatePodsUtility() {
      this.errorMessage = '';
      this.errorTranslatable = '';

      if (this.suitablePods.length === 0) {
        const serviceStore = useServiceStore();
        serviceStore.resetUtilityPods();
        return;
      }

      const payload = {
        serviceAlias: this.mediaConverterService.alias,
        pods: this.suitablePods,
      };

      const serviceStore = useServiceStore();
      serviceStore.updatePodsUtility(payload);
    },
    runJobs: async function() {
      const validForm = await this.$refs.form.validate();
      if (!validForm.valid) {
        return;
      }

      this.errorMessage = "";
      this.successMessage = "";

      this.preventDoubleclick = true;
      setTimeout(() => {
        this.preventDoubleclick = false;
      }, 10000);

      const payload = {
        serviceAlias: this.mediaConverterService.alias,
        utilityPods: this.runnablePods,
        params: {
          format: this.selectedFormat,
          quality: this.selectedQuality
        }
      }

      console.log('payload', payload);

      const serviceStore = useServiceStore();
      try {
        await serviceStore.runService(payload);
        if (this.utilityPodsErrors.length === 0) {
          this.successMessage = this.$t('routes.pod.menuMediaConverter.operationQueued');

          setTimeout(() => {
            this.successMessage = '';
            if (this.dialog === true) {
              this.updatePodsUtility();
            }
          }, 10000);
        }

        this.closeButtonLabel = this.$t('common.close');
      } catch (error) {
        if (error && error.message) {
          this.errorMessage = this.$localizeMessage(error.message);
        } else {
          console.log(error);
          this.errorMessage = this.$t('backend.system-error');
        }

        this.preventDoubleclick = false;
      }
    },
  },
  computed: {
    mediaConverterService: function() {
      const serviceStore = useServiceStore();
      return serviceStore.getMediaConverter;
    },
    isRunJobsDisabled: function() {
      return this.utilityPodsErrors.length > 0 || this.preventDoubleclick === true;
    },
    runnablePods: function() {
      const serviceStore = useServiceStore();
      return serviceStore.getRunnablePods;
    },
    utilityPodsErrors: function() {
      const serviceStore = useServiceStore();
      return serviceStore.getUtilityPodsErrors;
    },
    suitablePods: function() {
      const podStore = usePodStore();
      return podStore.getSelectedImagePods;
    },
    imageFormats: function() {
      return ['png', 'jpg', 'webp', 'gif'];
    },
    imageQuality: function() {
      return [
        {
          name: this.$t('routes.pod.menuMediaConverter.imageLowest'),
          value: "lowest",
        },{
          name: this.$t('routes.pod.menuMediaConverter.imageLow'),
          value: "low",
        },
        {
          name: this.$t('routes.pod.menuMediaConverter.imageAverage'),
          value: "average",
        },
        {
          name: this.$t('routes.pod.menuMediaConverter.imageGood'),
          value: "good",
        },
        {
          name: this.$t('routes.pod.menuMediaConverter.imageBest'),
          value: "best",
        },
      ];
    }
  }
}
</script>

<style>

</style>
